import { Typography } from '@mui/material';
import type { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid } from '@mui/x-data-grid';
import { GridPagination } from './GridPagination';
import type { UsePaginationResult } from 'hooks/usePagination';
import { GridContainer } from './grid.styles';
import type { GridEventListener } from '@mui/x-data-grid/models/events';

interface BaseGridProps<T> {
  rows: T[];
  columns: GridColDef[];
  count: number;
  isLoading: boolean;
  pagination: UsePaginationResult;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (selectedRows: GridRowSelectionModel) => void;
  getRowId: (row: T) => string | number;
  onRowClick?: GridEventListener<'rowClick'>;
  emptyPlaceholder?: string;
}

export const BaseGrid =
  <T extends { id: string | number }>
  ({
     rows,
     columns,
     count,
     isLoading,
     pagination,
     rowSelectionModel,
     onRowSelectionModelChange,
     getRowId,
     onRowClick,
     emptyPlaceholder,
   }: BaseGridProps<T>) => {
    if (!rows?.length) {
      return (
        <Typography
          sx={(t) => ({ fontSize: t.typography.h1, color: t.palette.grey.A200 })}
        >
          {emptyPlaceholder ?? 'No data received.'}
        </Typography>
      );
    }

    return (
      <GridContainer>
        <DataGrid
          rows={rows}
          columns={columns}
          checkboxSelection={false}
          rowSelectionModel={rowSelectionModel}
          loading={isLoading}
          onRowSelectionModelChange={onRowSelectionModelChange}
          onRowClick={onRowClick}
          hideFooterPagination
          getRowId={getRowId}
          isRowSelectable={() => false}
          sx={onRowClick ? { '& .MuiDataGrid-row': { cursor: 'pointer' } } : {}}
          slots={{
            footer: () => (
              <GridPagination
                count={count}
                isLoading={isLoading}
                page={pagination.page}
                perPage={pagination.perPage}
                handlePageChange={pagination.handlePageChange}
                handleRowsPerPageChange={pagination.handleRowsPerPageChange}
              />
            ),
          }}
        />
      </GridContainer>
    );
  };
